import React, { useState, useEffect } from 'react';
import { Plus, Send, Users } from 'lucide-react';

const DistributionList = ({ onSubmit, onNext, onPrevious, initialData = {} }) => {
  const [recipients, setRecipients] = useState(initialData.recipients || []);
  const [messageTemplate, setMessageTemplate] = useState(initialData.messageTemplate || {
    subject: 'Sustainability Report {FY} - {Company}',
    body: `Dear {recipient},

We are pleased to share our {FY} Sustainability Report with you.

You can access the report at: {link}

Best regards,
{Company} Team`
  });
  const [attachments, setAttachments] = useState(initialData.attachments || {
    includePDF: true,
    excludeXBRL: true
  });
  const [sendTiming, setSendTiming] = useState(initialData.sendTiming || 'now');
  const [notificationsSent, setNotificationsSent] = useState(initialData.notificationsSent || false);
  const [sendLog, setSendLog] = useState(initialData.sendLog || null);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    // Load initial data when component mounts
  }, [initialData]);

  const addRecipient = () => {
    const newRecipient = {
      id: Date.now(),
      email: '',
      name: '',
      role: '',
      status: 'pending'
    };
    setRecipients([...recipients, newRecipient]);
  };

  const removeRecipient = (id) => {
    setRecipients(recipients.filter(r => r.id !== id));
  };

  const updateRecipient = (id, field, value) => {
    setRecipients(recipients.map(r => 
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  const addRecipientsFromText = (text) => {
    const emails = text.split(/[,\n]/).map(email => email.trim()).filter(email => email);
    const newRecipients = emails.map(email => ({
      id: Date.now() + Math.random(),
      email,
      name: '',
      role: '',
      status: 'pending'
    }));
    setRecipients([...recipients, ...newRecipients]);
  };

  const sendNotifications = async () => {
    setIsSending(true);
    
    // Simulate sending notifications
    await new Promise(resolve => setTimeout(resolve, 3000));
    
    const log = {
      sentAt: new Date().toISOString(),
      total: recipients.length,
      delivered: recipients.length - 2,
      bounced: 2,
      details: recipients.map(r => ({
        email: r.email,
        status: Math.random() > 0.8 ? 'bounced' : 'delivered',
        timestamp: new Date().toISOString()
      }))
    };
    
    setSendLog(log);
    setNotificationsSent(true);
    setIsSending(false);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'text-green-600';
      case 'bounced':
        return 'text-red-600';
      case 'pending':
        return 'text-gray-500';
      default:
        return 'text-gray-500';
    }
  };


  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Distribution List</h2>
        <p className="text-gray-600">Send notifications to recipients</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Recipients & Message */}
        <div className="space-y-6">
          {/* Recipients */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Recipients</h3>
              <button
                onClick={addRecipient}
                className="flex items-center px-3 py-1 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Recipient
              </button>
            </div>

            {/* Bulk Add */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bulk Add (comma or newline separated)
              </label>
              <textarea
                placeholder="email1@example.com, email2@example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                rows="3"
                onBlur={(e) => {
                  if (e.target.value.trim()) {
                    addRecipientsFromText(e.target.value);
                    e.target.value = '';
                  }
                }}
              />
            </div>

            {/* Recipients List */}
            {recipients.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No recipients added yet. Click "Add Recipient" to get started.</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {recipients.map((recipient) => (
                  <div key={recipient.id} className="border border-gray-200 rounded-lg p-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <input
                        type="email"
                        value={recipient.email}
                        onChange={(e) => updateRecipient(recipient.id, 'email', e.target.value)}
                        placeholder="Email"
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={recipient.name}
                        onChange={(e) => updateRecipient(recipient.id, 'name', e.target.value)}
                        placeholder="Name (optional)"
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                      <input
                        type="text"
                        value={recipient.role}
                        onChange={(e) => updateRecipient(recipient.id, 'role', e.target.value)}
                        placeholder="Role (optional)"
                        className="px-2 py-1 border border-gray-300 rounded"
                      />
                    </div>
                    <button
                      onClick={() => removeRecipient(recipient.id)}
                      className="text-red-500 text-xs mt-2"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Message Template */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Message Template</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={messageTemplate.subject}
                  onChange={(e) => setMessageTemplate(prev => ({ ...prev, subject: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Subject line"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Body
                </label>
                <textarea
                  value={messageTemplate.body}
                  onChange={(e) => setMessageTemplate(prev => ({ ...prev, body: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="6"
                  placeholder="Email body"
                />
              </div>
              <div className="text-xs text-gray-500">
                <p>Available variables: {'{company}, {FY}, {link}, {recipient}'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Attachments & Send */}
        <div className="space-y-6">
          {/* Attachments */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Attachments</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={attachments.includePDF}
                  onChange={(e) => setAttachments(prev => ({ ...prev, includePDF: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Include PDF (optional)</span>
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={attachments.excludeXBRL}
                  onChange={(e) => setAttachments(prev => ({ ...prev, excludeXBRL: e.target.checked }))}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Exclude XBRL by default (add checkbox)</span>
              </label>
            </div>
          </div>

          {/* Send Timing */}
          <div>
            <h3 className="text-lg font-semibold text-gray-700 mb-3">Send Timing</h3>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sendTiming"
                  value="now"
                  checked={sendTiming === 'now'}
                  onChange={(e) => setSendTiming(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Send now</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="sendTiming"
                  value="embargo"
                  checked={sendTiming === 'embargo'}
                  onChange={(e) => setSendTiming(e.target.value)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Send at embargo time</span>
              </label>
            </div>
          </div>

          {/* Send Button */}
          <div className="text-center">
            <button
              onClick={sendNotifications}
              disabled={isSending || recipients.length === 0}
              className={`flex items-center justify-center w-full px-6 py-4 rounded-md transition-colors ${
                isSending || recipients.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <Send className="w-5 h-5 mr-2" />
              {isSending ? 'Sending...' : `Send Notifications (${recipients.length})`}
            </button>
          </div>

          {/* Send Log */}
          {notificationsSent && sendLog && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-3">Notifications Sent</h3>
              <div className="space-y-2 text-sm text-green-700">
                <p><strong>Sent at:</strong> {new Date(sendLog.sentAt).toLocaleString()}</p>
                <p><strong>Total:</strong> {sendLog.total}</p>
                <p><strong>Delivered:</strong> {sendLog.delivered}</p>
                <p><strong>Bounced:</strong> {sendLog.bounced}</p>
              </div>
              
              {/* Detailed Log */}
              <div className="mt-4">
                <h4 className="font-medium text-green-800 mb-2">Delivery Details</h4>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {sendLog.details.map((detail, index) => (
                    <div key={index} className="flex justify-between text-xs">
                      <span>{detail.email}</span>
                      <span className={getStatusColor(detail.status)}>
                        {detail.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-3">Configuration Summary</h3>
            <div className="space-y-2 text-sm text-gray-600">
              <p><strong>Recipients:</strong> {recipients.length}</p>
              <p><strong>PDF Attachment:</strong> {attachments.includePDF ? 'Yes' : 'No'}</p>
              <p><strong>XBRL Attachment:</strong> {attachments.excludeXBRL ? 'Excluded' : 'Included'}</p>
              <p><strong>Send Timing:</strong> {sendTiming === 'now' ? 'Immediate' : 'At embargo'}</p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default DistributionList;
